import { AlertCircle, DotsVertical, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import ActionTooltip from 'src/components/ActionTooltip';
import BadgeCmp from 'src/components/BadgeCmp';
import Radio from 'src/components/Radio/Radio';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { BadgeType } from 'src/interface/common';
import { RootState } from 'src/redux/store';
import { getFormattedNumber, getShortName, onError } from 'src/utils/CommonFunctions';

import checkCircle from '../../../assets/icons/filled-icons/check-circle.svg';
import { QuoteColumns, QuoteData } from '../Component/SelectQuoteBlock';
import { getRateCurrencyDisplay } from '../myQuote.helpers';
import { calculateBusinessDays } from '../quotingDashboard.helpers';

import { isExpiredQuoteRate } from './quoteDetails.helper';

interface SelectQuoteTableRowProps {
    data?: QuoteData;
    currentIndex?: number;
    hideBookOrderButton?: boolean;
    quotePickupDate?: string;
    quoteColumns?: QuoteColumns | null;
    onDuplicate?: (data: QuoteData) => void;
    onRequestRate?: (data: QuoteData) => void;
    /**
     * Read-only mode for public share-link viewers — drops the trailing
     * action cell (expired-rate 3-dot menu) entirely. Must match the head
     * cell list in the parent SelectQuoteBlock.
     */
    isReadOnly?: boolean;
    // User decision — customer can pick only ONE quote (radio, not checkbox).
    selectedId?: number | null;
    onSelect?: (id: number) => void;
    /**
     * Whether any row in the currently displayed batch is expired — mirrors Orders'
     * hasPriorityOrder pattern. The rateAction cell only ever renders content for expired
     * rates, so the parent drops the column (and this cell) entirely when nothing qualifies,
     * instead of reserving an always-empty 48px ahead of the trailing radio column.
     */
    hasExpiredRate?: boolean;
}

const SelectQuoteTableRow: React.FC<SelectQuoteTableRowProps> = ({
    data,
    currentIndex = 0,
    hideBookOrderButton = false,
    quotePickupDate,
    quoteColumns,
    onDuplicate,
    onRequestRate,
    isReadOnly = false,
    selectedId,
    onSelect,
    hasExpiredRate = true,
}) => {
    const { t } = useTranslation();
    const carrierNameRef = useRef<HTMLHeadingElement>(null);
    const { appearance } = useSelector((state: RootState) => state.basic);
    const orgBranding = useOrgBranding();
    const userTimezone = appearance?.timezone || 'UTC';

    const businessDays = useMemo(() => {
        const currentDate = moment.tz(userTimezone).startOf('day');

        // Use the parent quote's pickup date (quotePickupDate), same as the reference component uses addressDetails?.pickupDate
        const pickupDateValue = quotePickupDate ? moment.utc(quotePickupDate) : currentDate;

        const adjustedPickupDate = pickupDateValue.isBefore(currentDate, 'day') ? currentDate : pickupDateValue;

        const deliveryDate = moment.utc(data.projectedDeliveryDate);

        const businessDaysCalculatedResponse = calculateBusinessDays(adjustedPickupDate, deliveryDate, {
            skipStartDate: false,
        }) as any;

        return businessDaysCalculatedResponse;
    }, [quotePickupDate, data.projectedDeliveryDate, userTimezone]);

    const tagType: Record<string, BadgeType> = {
        Fastest: 'lightblue',
        Cheapest: 'primary',
        Premium: 'orange',
    };

    // Resolves the carrier name: prefers quoteCarrier.carrierName (denormalized),
    // falls back to carrier.name from JOIN. Returns '-' for empty string.
    const resolveCarrierName = () => {
        const name = data?.carrierName ?? data?.name;
        if (name === '') return '-';

        return name || '-';
    };

    const highlightClass = data?.isBookedByCustomer || data.id === data?.toBookQuoteCarrierId ? 'bg-utilityGray100' : '';

    // Client decision — green checkmark column (right after Total Cost) only once a quote is
    // actually booked/to-book (same condition highlightClass already uses). Deliberately NOT tied
    // to the live radio selection (selectedId) — showing it before booking is complete was reported
    // as a bug, since at that point nothing has actually been selected/booked yet.
    const isSelectedQuote = !!highlightClass;

    // Returns false only when the column is explicitly set to false; defaults to visible when null/undefined.
    const isColumnVisible = (key: string) => quoteColumns?.[key] !== false;

    // We use data.status (joined from quotes table) rather than Redux addressDetails
    // to avoid a dependency on when addressDetails was last populated.
    const isExpiredRate = useMemo(() => isExpiredQuoteRate(data, userTimezone), [data, userTimezone]);

    // Expired rates can't be booked (see SelectQuoteBlock's handleBookOrderClick guard) —
    // disabling selection here stops the customer from picking one in the first place.
    // User decision — a single-select radio (was a multi-select checkbox), shared `inputName`
    // across every row so the browser's own radio-group semantics stay correct alongside the
    // controlled `checked`/`onChangeFunc` state SelectQuoteBlock drives.
    const selectRadio = (
        <Radio
            id={`selectQuote-${data?.id}`}
            inputName="selectQuote"
            value={data?.id}
            checked={selectedId === Number(data?.id)}
            onChangeFunc={() => onSelect?.(Number(data?.id))}
            disabled={isExpiredRate}
        />
    );

    return (
        <>
            <td className={`${highlightClass} min-w-[82px] max-w-[82px]`}>
                <div className="flex items-center gap-x-1">
                    <p className="text-grayLight900 font-medium">Option {currentIndex + 1}</p>
                    {/* Show the expired indicator here when carrier column is hidden */}
                    {isExpiredRate && !isColumnVisible('carrierName') && (
                        <TooltipCmp position="top" message={t('MyQuote.Carrier_Quote_Expired')}>
                            <AlertCircle className="w-4 h-4 text-fgErrorPrimary flex-none cursor-pointer" />
                        </TooltipCmp>
                    )}
                </div>
            </td>
            {/* Carrier Name — controlled by quoteColumns.carrierName */}
            {isColumnVisible('carrierName') && (
                <td className={`${highlightClass} min-w-[170px] max-w-[170px]`}>
                    <div className={`flex items-center gap-x-2 ${isExpiredRate ? 'opacity-50' : ''}`}>
                        <div className="flex-none relative">
                            {data?.image && data?.imageUrl ? (
                                <img src={`${data.imageUrl}${data.image}`} alt={resolveCarrierName()} className="table-profile-icn-circle-xs rounded-md object-contain" onError={onError} />
                            ) : resolveCarrierName() === orgBranding.name || resolveCarrierName() === 'Western Alliance' ? (
                                // Org presented as the carrier (visibility off) with no uploaded brand logo — black WAL icon fallback
                                <div className="table-profile-customer-circle-xs rounded-md !bg-utilityBlack flex items-center justify-center p-0.5">
                                    <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                                </div>
                            ) : (
                                <div className="table-profile-customer-circle-xs rounded-md">{getShortName(data?.carrierName ?? data?.name ?? '')}</div>
                            )}
                            {isExpiredRate && (
                                <div className="absolute -bottom-1 -right-1">
                                    <TooltipCmp position="top" message={t('MyQuote.Carrier_Quote_Expired')}>
                                        <AlertCircle className="w-3.5 h-3.5 text-fgErrorPrimary bg-white rounded-full" />
                                    </TooltipCmp>
                                </div>
                            )}
                        </div>
                        <TruncateTextCmp refName={carrierNameRef} message={resolveCarrierName()}>
                            <h6 className="text-grayLight900 font-medium block truncate" ref={carrierNameRef}>
                                {resolveCarrierName()}
                            </h6>
                        </TruncateTextCmp>
                    </div>
                </td>
            )}
            <td className={`${highlightClass} min-w-[100px] max-w-[100px]`}>
                {tagType?.[data?.tag] ? (
                    <BadgeCmp type={tagType[data.tag] as BadgeType} style="modern">
                        {data.tag}
                    </BadgeCmp>
                ) : (
                    '-'
                )}
            </td>
            {isColumnVisible('shippingMode') && (
                <td className={`${highlightClass} min-w-[82px] max-w-[82px]`}>
                    <p className="text-textSecondary font-medium">{data?.shippingMode || '-'}</p>
                </td>
            )}
            {isColumnVisible('shippingServiceLevel') && (
                <td className={`${highlightClass} min-w-[108px] max-w-[108px]`}>
                    <p className="text-textSecondary font-medium">{data?.shippingServiceLevel || '-'}</p>
                </td>
            )}
            {isColumnVisible('transitTime') && (
                <td className={`${highlightClass} min-w-[108px] max-w-[108px]`}>
                    {data?.projectedDeliveryDate && businessDays > 0 ? (
                        <p className="text-textSecondary font-medium">
                            Est. {businessDays} {businessDays === 1 ? 'day' : 'days'}
                        </p>
                    ) : (
                        <p className="text-textSecondary font-medium">-</p>
                    )}
                </td>
            )}
            <td className={`${highlightClass} min-w-[108px] max-w-[108px]`}>
                {/* Currency follows the quote's billing account (getRateCurrencyDisplay) — the same rule
                    invoicing applies. Label was previously hardcoded to CAD. */}
                <p className="text-textSecondary font-medium">
                    ${getFormattedNumber(getRateCurrencyDisplay(data).amount) || t('QuotingHub.SelectQuote.NA')} {getRateCurrencyDisplay(data).label}
                </p>
            </td>
            {/* Checkmark only ever renders once booked/to-book — hidden entirely while still
                choosing (see SelectQuoteBlock's visibleHeadCells) so an always-empty cell doesn't
                leave dead space ahead of the trailing radio column. */}
            {hideBookOrderButton && (
                <td className={`${highlightClass} w-[40px] min-w-[40px] max-w-[40px]`}>
                    {isSelectedQuote && (
                        <div className="flex justify-end">
                            <img src={checkCircle} alt="success" className="w-4 h-4" />
                        </div>
                    )}
                </td>
            )}
            {/* Client decision — Order ID moved to the page header (linked "#<order code>" button
                next to Quote Actions — see QuoteDetail/index.tsx), so this trailing column is now
                ONLY the expired-rate action menu. Dropped once booked (hideBookOrderButton), or
                when hasExpiredRate is false (nothing in the batch is expired, so the column would
                only ever render empty) — that menu never applied there anyway, since Duplicate/
                Re-quote only make sense while still choosing a rate. Must match the head cell list
                in the parent SelectQuoteBlock. */}
            {!isReadOnly && !hideBookOrderButton && hasExpiredRate && (
                <td className={`${highlightClass} w-[40px] min-w-[40px] max-w-[40px]`}>
                    {isExpiredRate && (
                        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                            {data?.pendingRequestTypes?.length > 0 ? (
                                /* Customer already submitted a request — replace the 3-dot menu with an info
                                 * icon. Message is type-aware: re_quote / duplicate / both. */
                                <TooltipCmp
                                    position="top"
                                    message={
                                        data.pendingRequestTypes.includes('re_quote') && data.pendingRequestTypes.includes('duplicate')
                                            ? t('MyQuote.Pending_Requote_And_Duplicate')
                                            : data.pendingRequestTypes.includes('re_quote')
                                            ? t('MyQuote.Pending_Requote')
                                            : t('MyQuote.Pending_Duplicate')
                                    }
                                >
                                    <InfoCircle className="w-4 h-4 text-primary cursor-pointer" />
                                </TooltipCmp>
                            ) : (
                                /* No pending request — show the 3-dot dropdown with Duplicate and Re-quote. */
                                <ActionTooltip
                                    message={
                                        <ul className="action-tooltip-select min-w-[110px]">
                                            <li className="action-tooltip-select-item" onClick={() => onDuplicate?.(data)}>
                                                <p className="action-tooltip-select-item-title">{t('MyQuote.Duplicate')}</p>
                                            </li>
                                            <li className="action-tooltip-select-item" onClick={() => onRequestRate?.(data)}>
                                                <p className="action-tooltip-select-item-title">{t('MyQuote.Re_quote')}</p>
                                            </li>
                                        </ul>
                                    }
                                    type="light"
                                    tooltipPosition="right"
                                    leftAlignOffset="!translate-x-0"
                                    parentClassName="action-tooltip-select-wrap"
                                    actionTooltipCustomClass="action-tooltip-without-arrow"
                                >
                                    <DotsVertical className="w-4 h-4 text-grayLight600 cursor-pointer" />
                                </ActionTooltip>
                            )}
                        </div>
                    )}
                </td>
            )}
            {/* User decision — single-select radio moved to the right end (was a leading
                multi-select checkbox column). Must match SelectQuoteBlock's trailing
                'selectQuote' head cell. */}
            {!isReadOnly && !hideBookOrderButton && (
                <td className={`${highlightClass} !pl-2 w-[40px] min-w-[40px] max-w-[40px]`} onClick={(e) => e.stopPropagation()}>
                    {isExpiredRate ? (
                        <TooltipCmp position="top" message={t('MyQuote.Carrier_Quote_Expired_Book_Blocked')}>
                            <span className="inline-flex">{selectRadio}</span>
                        </TooltipCmp>
                    ) : (
                        selectRadio
                    )}
                </td>
            )}
        </>
    );
};
export default SelectQuoteTableRow;
